import forge from 'apis/forge';
import ActionButton from 'components/Widget/ActionButton';
import { IconType } from 'components/Icon';
import { Frame, FrameStatus } from 'dragonmouse-shared/interfaces';
import { runExportWithToast } from 'components/Widget/ExportReadyToast';
import { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveScene, selectFrames } from 'redux/db/reducers';
import { blobToBase64, packageFramesSvgAsset } from 'redux/db/util';
import { RootReducer } from 'redux/reducers';
import { buildExportFilename } from 'utils';
import * as Sentry from '@sentry/react';

type Format = 'png' | 'svg' | 'html' | 'html+images';

interface Props {
	label: string;
	format: Format;
	frameIds: string[];
}

// Single-frame raw-format exports get the asset's own extension; anything that
// the server returns as a zip uses '.zip'. html+images is always a zip.
const filenameForExport = (
	format: Format,
	frameCount: number,
	activeSceneTitle: string | null | undefined
): string => {
	if (format === 'html+images') {
		return buildExportFilename(activeSceneTitle, 'html-bundle', 'assets');
	}
	const ext = frameCount === 1 ? format : 'zip';
	const token = frameCount === 1 ? format : `${format}s`;
	return buildExportFilename(activeSceneTitle, token, format, ext);
};

const pendingLabelFor = (format: Format, frameCount: number): string => {
	if (format === 'html+images') return 'Packaging HTML bundle with reference images…';
	const label = format.toUpperCase();
	return frameCount === 1 ? `Preparing ${label}…` : `Packaging ${label}s…`;
};

const ExportAssetsAction = ({ label, format, frameIds }: Props) => {
	const [isExporting, setIsExporting] = useState(false);
	const { allFrames, activeScene } = useSelector(
		(r: RootReducer) => ({
			allFrames: selectFrames(r.db),
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
		}),
		shallowEqual
	);

	const frames: Frame[] = frameIds
		.map((id) => allFrames.find((f) => f.id === id))
		.filter((f): f is Frame => !!f);
	const allReady =
		frames.length === frameIds.length &&
		frames.every((f) => f.metadata.status === FrameStatus.READY);

	const onClick = async () => {
		if (isExporting) return;
		if (frames.length === 0) {
			toast.error('No matching frames to export');
			return;
		}
		if (frames.length < frameIds.length) {
			toast.error('Some frames are no longer available.');
			return;
		}
		if (!allReady) return;

		setIsExporting(true);
		const filename = filenameForExport(format, frames.length, activeScene?.title);
		try {
			await runExportWithToast({
				pendingLabel: pendingLabelFor(format, frames.length),
				errorLabel: 'Export failed. Please try again.',
				run: async () => {
					if (format === 'svg') {
						const { blob, extension } = await packageFramesSvgAsset(frames);
						const { exportId } = await forge.exportPrebuiltAsset({
							data: await blobToBase64(blob),
							filename,
							extension,
							label: 'Frame SVG',
						});
						return { exportId, filename };
					}
					const { exportId } = await forge.exportFramesAssets({
						frameIds: frames.map((f) => f.id),
						format,
						filename,
					});
					return { exportId, filename };
				},
			});
		} catch (e) {
			Sentry.captureException(e);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<ActionButton
			icon={IconType.DOWNLOAD}
			label={label}
			loadingLabel="Exporting frames…"
			isLoading={isExporting}
			disabledReason={!allReady ? 'Waiting for frames to finish generating.' : undefined}
			onClick={onClick}
		/>
	);
};

export default ExportAssetsAction;
